import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { AlertTriangle, CheckCircle2, Loader2, MessageCircle, Plus, RefreshCw, Send, Trash2, Unplug } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { whatsappApi, normalizeWhatsAppPhone, getWhatsAppTemplateVariables, fillWhatsAppTemplate, buildWhatsAppSendPayload, extractWhatsAppErrors } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import WhatsAppAutomation from "@/components/WhatsAppAutomation";

const EMPTY_VALUES = { name: "", company: "", phone: "", link: "" };
const STALE_REQUEST = Symbol("stale WhatsApp request");

function FieldError({ children }) {
  return children ? <p className="mt-1 text-xs text-rose-700" role="alert">{children}</p> : null;
}

function ErrorSummary({ messages, className = "" }) {
  return messages?.length ? (
    <div role="alert" className={`rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 ${className}`}>
      {messages.map((message, index) => <p key={`${index}-${message}`}>{message}</p>)}
    </div>
  ) : null;
}

function formatStatus(status) {
  return String(status || "Unknown").replace(/[_-]+/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function WhatsAppSettings() {
  const { can } = useAuth();
  const canManage = can("manage_settings");
  const [companyId, setCompanyId] = useState(() => localStorage.getItem("vg_company") || "");
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState("");
  const [connectionErrors, setConnectionErrors] = useState({ fields: {}, messages: [] });
  const [disconnectNotice, setDisconnectNotice] = useState("");
  const [phone, setPhone] = useState("");
  const [connectionBusy, setConnectionBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templateError, setTemplateError] = useState({ fields: {}, messages: [] });
  const [templateBusy, setTemplateBusy] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateForm, setTemplateForm] = useState({ name: "", message: "" });
  const [composerMessage, setComposerMessage] = useState("");
  const [values, setValues] = useState(EMPTY_VALUES);
  const [destination, setDestination] = useState("");
  const [recipientConsent, setRecipientConsent] = useState(false);
  const [sendErrors, setSendErrors] = useState({ fields: {}, messages: [] });
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [secondsUntilExpiry, setSecondsUntilExpiry] = useState(null);
  const phoneTouched = useRef(false);
  const pendingRequests = useRef(new Set());
  const sendLock = useRef(false);
  const mounted = useRef(false);

  useEffect(() => {
    const syncCompany = () => setCompanyId(localStorage.getItem("vg_company") || "");
    window.addEventListener("storage", syncCompany);
    window.addEventListener("companychange", syncCompany);
    const watcher = window.setInterval(syncCompany, 750);
    return () => {
      window.clearInterval(watcher);
      window.removeEventListener("storage", syncCompany);
      window.removeEventListener("companychange", syncCompany);
    };
  }, []);

  const requestForCompany = async (request) => {
    if (!canManage || !mounted.current) return STALE_REQUEST;
    const requestedCompany = companyId;
    if ((localStorage.getItem("vg_company") || "") !== requestedCompany) {
      setCompanyId(localStorage.getItem("vg_company") || "");
      return STALE_REQUEST;
    }
    const controller = new AbortController();
    pendingRequests.current.add(controller);
    try {
      const response = await request(controller.signal);
      if (controller.signal.aborted || !mounted.current || (localStorage.getItem("vg_company") || "") !== requestedCompany) return STALE_REQUEST;
      return response?.data;
    } catch (error) {
      if (controller.signal.aborted || !mounted.current || (localStorage.getItem("vg_company") || "") !== requestedCompany) return STALE_REQUEST;
      throw error;
    } finally {
      pendingRequests.current.delete(controller);
    }
  };

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    for (const controller of pendingRequests.current) controller.abort();
    pendingRequests.current.clear();
    phoneTouched.current = false;
    setStatus(null);
    setStatusError("");
    setConnectionErrors({ fields: {}, messages: [] });
    setDisconnectNotice("");
    setPhone("");
    setConnectionBusy(false);
    setTemplates([]);
    setTemplatesLoading(Boolean(companyId));
    setTemplateError({ fields: {}, messages: [] });
    setEditingTemplateId(null);
    setSelectedTemplateId("");
    setTemplateForm({ name: "", message: "" });
    setComposerMessage("");
    setValues(EMPTY_VALUES);
    setDestination("");
    setRecipientConsent(false);
    setSendErrors({ fields: {}, messages: [] });
    setSending(false);
    sendLock.current = false;
    setSendResult(null);
    return () => {
      for (const controller of pendingRequests.current) controller.abort();
      pendingRequests.current.clear();
    };
  }, [companyId]);

  useEffect(() => {
    if (!canManage || !companyId) {
      setTemplatesLoading(false);
      return undefined;
    }
    let active = true;
    let inFlight = false;
    const loadStatus = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const data = await requestForCompany((signal) => whatsappApi.getStatus({ signal }));
        if (data === STALE_REQUEST) return;
        if (active && data == null && (localStorage.getItem("vg_company") || "") === companyId) {
          setStatus(null);
          setStatusError("The WhatsApp status service returned an empty response.");
        } else if (active && data && typeof data.status !== "string") {
          setStatus(null);
          setStatusError("The WhatsApp status response did not include a connection status.");
        } else if (active && data) {
          setStatus(data);
          setStatusError("");
          if (!phoneTouched.current) setPhone(data.phone || "");
        }
      } catch (error) {
        if (active) {
          setStatus(null);
          setStatusError(extractWhatsAppErrors(error).messages.join(" "));
        }
      } finally {
        inFlight = false;
      }
    };
    loadStatus();
    const poll = window.setInterval(loadStatus, 5000);
    return () => {
      active = false;
      window.clearInterval(poll);
    };
    // requestForCompany intentionally uses this effect's company identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, canManage]);

  const loadTemplates = async () => {
    if (!companyId) return;
    setTemplatesLoading(true);
    setTemplateError({ fields: {}, messages: [] });
    try {
      const data = await requestForCompany((signal) => whatsappApi.getTemplates({ signal }));
      if (data === STALE_REQUEST) return;
      if (data == null) {
        if ((localStorage.getItem("vg_company") || "") === companyId) {
          setTemplateError({ fields: {}, messages: ["The WhatsApp templates service returned an empty response."] });
        }
        return;
      }
      if (!Array.isArray(data)) throw new Error("The WhatsApp templates response was invalid.");
      setTemplates(data);
    } catch (error) {
      if (mounted.current) setTemplateError(error.response
          ? extractWhatsAppErrors(error)
          : { fields: {}, messages: [error.message || "Could not load WhatsApp templates."] });
    } finally {
      if (mounted.current && (localStorage.getItem("vg_company") || "") === companyId) setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    if (!canManage || !companyId) {
      setTemplatesLoading(false);
      return undefined;
    }
    let active = true;
    const load = async () => {
      setTemplatesLoading(true);
      try {
        const data = await requestForCompany((signal) => whatsappApi.getTemplates({ signal }));
        if (data === STALE_REQUEST) return;
        if (active && data == null && (localStorage.getItem("vg_company") || "") === companyId) {
          setTemplateError({ fields: {}, messages: ["The WhatsApp templates service returned an empty response."] });
        } else if (active && data != null) {
          if (!Array.isArray(data)) throw new Error("The WhatsApp templates response was invalid.");
          setTemplates(data);
          setTemplateError({ fields: {}, messages: [] });
        }
      } catch (error) {
        if (active) setTemplateError(error.response
          ? extractWhatsAppErrors(error)
          : { fields: {}, messages: [error.message || "Could not load WhatsApp templates."] });
      } finally {
        if (active) setTemplatesLoading(false);
      }
    };
    load();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, canManage]);

  useEffect(() => {
    if (!status?.qr || !status.qr_expires_at) {
      setSecondsUntilExpiry(null);
      return undefined;
    }
    const update = () => setSecondsUntilExpiry(Math.max(0, Math.ceil((new Date(status.qr_expires_at).getTime() - Date.now()) / 1000)));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [status?.qr, status?.qr_expires_at]);

  if (!canManage) {
    return <Card className="p-6" data-testid="whatsapp-settings-denied">
      <p className="text-sm text-muted-foreground">You need the manage settings permission to configure WhatsApp.</p>
    </Card>;
  }

  const statusIsConnected = status?.status === "connected";
  const normalizedPhone = normalizeWhatsAppPhone(phone);
  const connectionPhoneError = connectionErrors.fields.phone
    || (phone && !normalizedPhone ? "Enter an international phone number: 7–15 digits, starting with a non-zero digit." : "");
  const currentTemplate = templates.find((item) => String(item.id) === String(selectedTemplateId));
  const normalizedDestination = normalizeWhatsAppPhone(destination);
  const destinationPhoneError = sendErrors.fields.phone
    || (destination && !normalizedDestination ? "Enter an international phone number: 7–15 digits, starting with a non-zero digit." : "");
  const composerVariables = getWhatsAppTemplateVariables(composerMessage);
  const missingComposerValues = composerVariables.filter((key) => !String(values[key] || "").trim());
  const previewMessage = fillWhatsAppTemplate(composerMessage, values);
  const qrIsFresh = Boolean(status?.qr && status?.qr_expires_at
    && Number.isFinite(new Date(status.qr_expires_at).getTime())
    && new Date(status.qr_expires_at).getTime() > Date.now());

  const applyTemplate = (template) => {
    setSelectedTemplateId(template ? String(template.id) : "");
    setEditingTemplateId(template?.id ?? null);
    setTemplateForm(template ? { name: template.name || "", message: template.message || "" } : { name: "", message: "" });
    setComposerMessage(template ? template.message || "" : "");
    setValues(EMPTY_VALUES);
    setSendResult(null);
    setSendErrors({ fields: {}, messages: [] });
  };

  const editComposerMessage = (message) => {
    setComposerMessage(message);
    if (selectedTemplateId) {
      setSelectedTemplateId("");
      setValues(EMPTY_VALUES);
    }
    setSendResult(null);
    setSendErrors({ fields: {}, messages: [] });
  };

  const connect = async () => {
    setConnectionErrors({ fields: {}, messages: [] });
    if (!normalizedPhone) {
      setConnectionErrors({ fields: { phone: "Enter an international phone number: 7–15 digits, starting with a non-zero digit." }, messages: [] });
      return;
    }
    setConnectionBusy(true);
    try {
      const data = await requestForCompany((signal) => whatsappApi.connect({ phone: normalizedPhone }, { signal }));
      if (data === STALE_REQUEST) return;
      if (data == null) {
        if ((localStorage.getItem("vg_company") || "") === companyId) {
          setConnectionErrors({ fields: {}, messages: ["The WhatsApp connection service returned an empty response."] });
        }
        return;
      }
      if (typeof data.status !== "string") {
        setStatus(null);
        setConnectionErrors({ fields: {}, messages: ["The WhatsApp connection response did not include a connection status."] });
        return;
      }
      setStatus(data);
      setDisconnectNotice("");
      setPhone(data.phone || normalizedPhone);
      phoneTouched.current = false;
      setStatusError("");
    } catch (error) {
      if (mounted.current) setConnectionErrors(extractWhatsAppErrors(error));
    } finally {
      if (mounted.current) setConnectionBusy(false);
    }
  };

  const disconnect = async () => {
    const accepted = window.confirm("Disconnect the WhatsApp linked device for this company? This will not log you out of the ERP.");
    if (!accepted) return;
    setConnectionBusy(true);
    setConnectionErrors({ fields: {}, messages: [] });
    try {
      const data = await requestForCompany((signal) => whatsappApi.disconnect({ signal }));
      if (data === STALE_REQUEST) return;
      if (data == null) {
        if ((localStorage.getItem("vg_company") || "") === companyId) {
          setConnectionErrors({ fields: {}, messages: ["The WhatsApp disconnect service returned an empty response."] });
        }
        return;
      }
      if (data.ok !== true) {
        setConnectionErrors({ fields: {}, messages: ["The WhatsApp service did not confirm the disconnect request."] });
        return;
      }
      const remoteLogoutMessage = data.remote_logout_confirmed === true
        ? "Remote logout was confirmed."
        : "Remote logout could not be confirmed.";
      const warning = typeof data.warning === "string" && data.warning.trim() ? ` ${data.warning.trim()}` : "";
      setDisconnectNotice(
        `WhatsApp was stopped on this server. ${remoteLogoutMessage} If the device still appears on your phone, remove it from WhatsApp → Linked devices.${warning}`
      );
      setStatus(null);
      setStatusError("Disconnect request accepted. Checking the current WhatsApp connection status…");
      const refreshed = await requestForCompany((signal) => whatsappApi.getStatus({ signal }));
      if (refreshed === STALE_REQUEST) return;
      if (refreshed && typeof refreshed.status === "string") {
        setStatus(refreshed);
        setStatusError("");
      } else {
        setStatusError("Disconnect was accepted, but the current WhatsApp status could not be retrieved.");
      }
    } catch (error) {
      if (mounted.current) setConnectionErrors(extractWhatsAppErrors(error));
    } finally {
      if (mounted.current) setConnectionBusy(false);
    }
  };

  const saveTemplate = async (event) => {
    event.preventDefault();
    setTemplateError({ fields: {}, messages: [] });
    if (!templateForm.name.trim() || !templateForm.message.trim()) {
      setTemplateError({ fields: {
        ...(!templateForm.name.trim() ? { name: "Enter a template name." } : {}),
        ...(!templateForm.message.trim() ? { message: "Enter a template message." } : {}),
      }, messages: [] });
      return;
    }
    if (templateForm.message.length > 4096) {
      setTemplateError({ fields: { message: "Messages must be 4096 characters or fewer." }, messages: [] });
      return;
    }
    setTemplateBusy(true);
    try {
      const payload = { name: templateForm.name.trim(), message: templateForm.message };
      const data = editingTemplateId == null
        ? await requestForCompany((signal) => whatsappApi.createTemplate(payload, { signal }))
        : await requestForCompany((signal) => whatsappApi.updateTemplate(editingTemplateId, payload, { signal }));
      if (data === STALE_REQUEST) return;
      if (data == null) {
        setTemplateError({ fields: {}, messages: ["The WhatsApp template service returned an empty response."] });
        return;
      }
      const id = data.id ?? editingTemplateId;
      await loadTemplates();
      if (id != null) {
        setSelectedTemplateId(String(id));
        setEditingTemplateId(id);
      }
      setTemplateForm({ name: payload.name, message: payload.message });
      setComposerMessage(payload.message);
      setValues(EMPTY_VALUES);
    } catch (error) {
      if (mounted.current) setTemplateError(extractWhatsAppErrors(error));
    } finally {
      if (mounted.current) setTemplateBusy(false);
    }
  };

  const deleteTemplate = async () => {
    if (editingTemplateId == null || !window.confirm(`Delete the “${templateForm.name}” WhatsApp template?`)) return;
    setTemplateBusy(true);
    setTemplateError({ fields: {}, messages: [] });
    try {
      const data = await requestForCompany((signal) => whatsappApi.deleteTemplate(editingTemplateId, { signal }));
      if (data === STALE_REQUEST) return;
      if (data == null || data.ok !== true) {
        setTemplateError({ fields: {}, messages: ["The WhatsApp template service did not confirm deletion."] });
        return;
      }
      setTemplates((items) => items.filter((item) => String(item.id) !== String(editingTemplateId)));
      applyTemplate(null);
    } catch (error) {
      if (mounted.current) setTemplateError(extractWhatsAppErrors(error));
    } finally {
      if (mounted.current) setTemplateBusy(false);
    }
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    if (sendLock.current || sending) return;
    setSendErrors({ fields: {}, messages: [] });
    const cleanDestination = normalizeWhatsAppPhone(destination);
    const fields = {};
    if (!cleanDestination) fields.phone = "Enter an international phone number: 7–15 digits, starting with a non-zero digit.";
    if (!previewMessage.trim()) fields.message = "Enter a message before sending.";
    else if (previewMessage.length > 4096) fields.message = "Messages must be 4096 characters or fewer.";
    if (!recipientConsent) fields.recipient_consent = "Confirm that this recipient opted in to receive WhatsApp messages.";
    missingComposerValues.forEach((key) => { fields[key] = `Enter a value for ${key}.`; });
    if (Object.keys(fields).length) {
      setSendErrors({ fields, messages: [] });
      return;
    }
    const payload = buildWhatsAppSendPayload({
      phone: cleanDestination,
      message: composerMessage,
      template: currentTemplate,
      values,
    });
    if (!window.confirm(`Send this WhatsApp message to +${cleanDestination}?\n\n${payload.message}`)) return;
    sendLock.current = true;
    setSending(true);
    setSendResult(null);
    try {
      const data = await requestForCompany((signal) => whatsappApi.send(payload, { signal }));
      if (data === STALE_REQUEST) return;
      if (!data) {
        setSendErrors({ fields: {}, messages: ["The WhatsApp service returned an empty message-submission response."] });
        return;
      }
      if (data.ok !== true || data.status !== "submitted") {
        setSendErrors({ fields: {}, messages: ["The WhatsApp service did not confirm that this message was submitted."] });
        return;
      }
      setSendResult(data);
    } catch (error) {
      if (mounted.current) setSendErrors(extractWhatsAppErrors(error));
    } finally {
      sendLock.current = false;
      if (mounted.current) setSending(false);
    }
  };

  const connectionStatusClass = statusIsConnected
    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
    : "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <div className="space-y-4" data-testid="whatsapp-settings">
      {!companyId && <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">Select a company to configure WhatsApp.</div>}

      <WhatsAppAutomation canManage={canManage} companyId={companyId} />

      <Card className="p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 font-semibold"><MessageCircle className="h-4 w-4 text-emerald-600" />WhatsApp linked-device connection</h3>
            <p className="mt-1 text-sm text-muted-foreground">Pair by scanning a QR code from WhatsApp on your phone. This is not the Cloud API.</p>
          </div>
          <span className={`rounded-full border px-3 py-1 text-xs font-medium ${connectionStatusClass}`} data-testid="whatsapp-connection-status">
            {status ? formatStatus(status.status) : "Loading status…"}
          </span>
        </div>

        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <div className="flex items-start gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>This uses unofficial linked-device pairing rather than WhatsApp’s official Cloud API. Unofficial integrations carry account and policy risks, and WhatsApp may revoke the linked session or account. The service needs an always-on server to stay available 24/7.</p>
          </div>
        </div>

        {status?.runtime_available === false && <div role="status" className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">The WhatsApp connection runtime is currently unavailable. No connection or QR code is being assumed.</div>}
        <p className="text-xs text-muted-foreground">Current publishing uses Autoscale, which can sleep while idle. WhatsApp availability 24/7 requires a Reserved VM with ongoing compute charges. No hosting change has been made; do not change deployment without approval.</p>
        {status?.error && <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">{typeof status.error === "string" ? status.error : JSON.stringify(status.error)}</div>}
        {statusError && <div role="status" className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">{statusError}</div>}
        <ErrorSummary messages={connectionErrors.messages} />
        {disconnectNotice && <div role="status" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="whatsapp-disconnect-notice">{disconnectNotice}</div>}

        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
          <div>
            <Label htmlFor="whatsapp-account-phone">WhatsApp account phone (international format)</Label>
            <Input id="whatsapp-account-phone" type="tel" autoComplete="tel" value={phone}
              onChange={(event) => {
                phoneTouched.current = true;
                setPhone(event.target.value);
                setConnectionErrors((current) => ({ ...current, fields: { ...current.fields, phone: "" } }));
              }}
              placeholder="+14155552671" className="mt-1.5" aria-invalid={Boolean(connectionPhoneError)}
              data-testid="whatsapp-account-phone" />
            <p className="mt-1 text-xs text-muted-foreground">Use country code and 7–15 digits. Spaces and hyphens are accepted.</p>
            <FieldError>{connectionPhoneError}</FieldError>
          </div>
          <div className="flex flex-wrap gap-2">
            {!statusIsConnected && <Button type="button" onClick={connect} disabled={connectionBusy || !companyId || !normalizedPhone} data-testid="whatsapp-connect">
              {connectionBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <MessageCircle className="mr-1 h-4 w-4" />}
              {connectionBusy ? "Connecting…" : status?.qr ? "Refresh QR" : "Connect WhatsApp"}
            </Button>}
            <Button type="button" variant="outline" onClick={disconnect} disabled={connectionBusy || !companyId} data-testid="whatsapp-disconnect">
                {connectionBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Unplug className="mr-1 h-4 w-4" />}
                {connectionBusy ? "Disconnecting…" : "Disconnect WhatsApp"}
            </Button>
          </div>
        </div>

        {statusIsConnected && status.phone && <p className="text-sm text-emerald-800">Connected WhatsApp account: <span className="font-mono">+{status.phone}</span></p>}
        {status?.connected_at && statusIsConnected && <p className="text-xs text-muted-foreground">Connected since {new Date(status.connected_at).toLocaleString()}</p>}
        {!statusIsConnected && qrIsFresh && secondsUntilExpiry > 0 && (
          <div className="flex flex-col items-center gap-3 rounded-lg border bg-white p-5 text-center" data-testid="whatsapp-qr-panel">
            <QRCodeSVG value={status.qr} size={220} level="M" includeMargin aria-label="WhatsApp linked-device pairing QR code" />
            <p className="text-sm font-medium">Scan this QR code from WhatsApp → Linked devices.</p>
            <p className="text-xs text-muted-foreground">QR code expires in {Math.floor(secondsUntilExpiry / 60)}:{String(secondsUntilExpiry % 60).padStart(2, "0")}. Refresh to request a new code.</p>
            <Button type="button" variant="outline" size="sm" onClick={connect} disabled={connectionBusy || !normalizedPhone}>
              <RefreshCw className="mr-1 h-4 w-4" />Refresh QR
            </Button>
          </div>
        )}
        {!statusIsConnected && status?.qr && !qrIsFresh && <p role="status" className="text-sm text-amber-800">The pairing QR code has expired. Request a new QR code to continue.</p>}
        {status && !statusIsConnected && !status.qr && status.status === "qr" && <p className="text-sm text-muted-foreground">Waiting for a pairing QR code from the WhatsApp runtime.</p>}
      </Card>

      <Card className="p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><h3 className="font-semibold">Message templates</h3><p className="mt-1 text-sm text-muted-foreground">Manage templates for this company. Use {"{name}"}, {"{company}"}, {"{phone}"}, or {"{link}"}.</p></div>
          <Button type="button" variant="outline" size="sm" onClick={() => applyTemplate(null)} disabled={templateBusy || !companyId} data-testid="whatsapp-new-template">
            <Plus className="mr-1 h-4 w-4" />New template
          </Button>
        </div>
        <ErrorSummary messages={templateError.messages} />
        <div>
          <Label htmlFor="whatsapp-template-select">Select a template</Label>
          <select id="whatsapp-template-select" value={selectedTemplateId}
            onChange={(event) => applyTemplate(templates.find((item) => String(item.id) === event.target.value) || null)}
            className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            disabled={templatesLoading || !companyId} data-testid="whatsapp-template-select">
            <option value="">No template selected</option>
            {templates.map((item) => <option key={item.id} value={String(item.id)}>{item.name}</option>)}
          </select>
          {templatesLoading && <p className="mt-1 text-xs text-muted-foreground">Loading company templates…</p>}
          <FieldError>{templateError.fields.template_id}</FieldError>
        </div>
        <form onSubmit={saveTemplate} className="grid gap-3">
          <div><Label htmlFor="whatsapp-template-name">Template name</Label>
            <Input id="whatsapp-template-name" value={templateForm.name} onChange={(event) => setTemplateForm((form) => ({ ...form, name: event.target.value }))}
              className="mt-1.5" maxLength={120} required aria-invalid={Boolean(templateError.fields.name)} data-testid="whatsapp-template-name" />
            <FieldError>{templateError.fields.name}</FieldError>
          </div>
          <div><Label htmlFor="whatsapp-template-message">Template message</Label>
            <Textarea id="whatsapp-template-message" value={templateForm.message}
              onChange={(event) => setTemplateForm((form) => ({ ...form, message: event.target.value }))}
              className="mt-1.5 min-h-28" maxLength={4096} required aria-invalid={Boolean(templateError.fields.message)} data-testid="whatsapp-template-message" />
            <div className="mt-1 flex justify-between gap-2 text-xs text-muted-foreground"><FieldError>{templateError.fields.message}</FieldError><span className={templateForm.message.length > 4096 ? "text-rose-700" : ""}>{templateForm.message.length}/4096</span></div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={templateBusy || !companyId || templateForm.message.length > 4096} data-testid="whatsapp-save-template">
              {templateBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}{templateBusy ? "Saving…" : editingTemplateId == null ? "Save template" : "Save changes"}
            </Button>
            {editingTemplateId != null && <Button type="button" variant="outline" onClick={deleteTemplate} disabled={templateBusy} data-testid="whatsapp-delete-template">
              <Trash2 className="mr-1 h-4 w-4" />Delete template
            </Button>}
          </div>
        </form>
      </Card>

      <Card className="p-5 space-y-4">
        <div><h3 className="font-semibold">Send a WhatsApp message</h3><p className="mt-1 text-sm text-muted-foreground">Messages are sent only after explicit confirmation. Submission does not mean delivery.</p></div>
        <ErrorSummary messages={sendErrors.messages} />
        <form onSubmit={sendMessage} className="grid gap-4">
          <div><Label htmlFor="whatsapp-recipient-phone">Destination phone (international format)</Label>
            <Input id="whatsapp-recipient-phone" type="tel" autoComplete="tel" value={destination} onChange={(event) => {
              setDestination(event.target.value);
              setSendErrors((current) => ({ ...current, fields: { ...current.fields, phone: "" } }));
            }}
              placeholder="+14155552671" className="mt-1.5" aria-invalid={Boolean(destinationPhoneError)} data-testid="whatsapp-recipient-phone" />
            <FieldError>{destinationPhoneError}</FieldError>
          </div>
          <div>
            <Label htmlFor="whatsapp-composer-message">Message</Label>
            <Textarea id="whatsapp-composer-message" value={composerMessage}
              onChange={(event) => editComposerMessage(event.target.value)}
              className="mt-1.5 min-h-32" maxLength={4096} aria-invalid={Boolean(sendErrors.fields.message)} data-testid="whatsapp-composer-message" />
            <div className="mt-1 flex justify-between text-xs text-muted-foreground"><FieldError>{sendErrors.fields.message}</FieldError><span>{previewMessage.length}/4096</span></div>
          </div>
          {composerVariables.length > 0 && <div className="grid gap-3 rounded-md border bg-slate-50 p-4 sm:grid-cols-2" data-testid="whatsapp-variable-fields">
            <p className="text-sm font-medium sm:col-span-2">Fill template variables</p>
            {composerVariables.map((key) => <div key={key}>
              <Label htmlFor={`whatsapp-value-${key}`}>{key[0].toUpperCase() + key.slice(1)}</Label>
              <Input id={`whatsapp-value-${key}`} value={values[key] || ""} onChange={(event) => setValues((current) => ({ ...current, [key]: event.target.value }))}
                className="mt-1.5" required data-testid={`whatsapp-value-${key}`} />
              <FieldError>{sendErrors.fields[key] || (!String(values[key] || "").trim() ? `Enter a value for ${key}.` : "")}</FieldError>
            </div>)}
          </div>}
          <div className="rounded-md border bg-slate-50 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preview</div>
            <p className="mt-2 whitespace-pre-wrap break-words text-sm" data-testid="whatsapp-message-preview">{previewMessage || "Your message preview will appear here."}</p>
          </div>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={recipientConsent} onChange={(event) => setRecipientConsent(event.target.checked)} className="mt-0.5 h-4 w-4 accent-brand-600"
              aria-invalid={Boolean(sendErrors.fields.recipient_consent)} data-testid="whatsapp-recipient-consent" />
            <span>The recipient has opted in to receive this WhatsApp message.</span>
          </label>
          <FieldError>{sendErrors.fields.recipient_consent}</FieldError>
          {sendResult && <div role="status" className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="whatsapp-send-result">
            <CheckCircle2 className="mr-1 inline h-4 w-4" />Message submitted{sendResult.message_id ? ` (ID: ${sendResult.message_id})` : ""}. This confirms submission only, not delivery.
          </div>}
          <div><Button type="submit" disabled={sending || !companyId || !statusIsConnected || !recipientConsent || !normalizedDestination || missingComposerValues.length > 0 || !previewMessage.trim() || previewMessage.length > 4096} data-testid="whatsapp-send">
            {sending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}{sending ? "Submitting…" : "Review & send"}
          </Button>
            {!statusIsConnected && <p className="mt-1 text-xs text-muted-foreground">Connect a WhatsApp linked device before sending.</p>}
          </div>
        </form>
      </Card>
    </div>
  );
}

export default WhatsAppSettings;